import React from 'react';

import { LightningIcon } from 'shared/assets/lightning-icon';
import { shouldHideLoyaltyPointsForRec } from 'shared/core/helpers/dispensaries';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { formatMaskedPhoneNumber } from 'src/components/kiosk-pro/kiosk-pro-shared/format-phone-number';
import type { KioskLoyaltyStatus } from 'src/hooks/use-loyalty';
import {
  Badge,
  InfoContainer,
  InfoText,
  LoginButton,
  LoginContainer,
  LoginText,
  Points,
  ThemedEmbeddedAccountIcon,
} from './consumer-info.styles';

type ConsumerSectionProps = {
  createPublicContextSessionLoading: boolean;
  guestPhoneNumber?: string | null;
  hasGuestPhone: boolean;
  hasLoyaltyIntegration: boolean;
  isUserLoggedIn: boolean;
  kioskProModalLogin: () => Promise<void>;
  loyaltyPoints: string;
  loyaltyProvider?: string | null;
  loyaltyStatus?: KioskLoyaltyStatus;
  onValidateLoyalty?: () => void;
  userFirstName?: string;
};

/**
 * Renders the consumer info section based on login state.
 */
export function ConsumerSection({
  createPublicContextSessionLoading,
  guestPhoneNumber,
  hasGuestPhone,
  hasLoyaltyIntegration,
  isUserLoggedIn,
  kioskProModalLogin,
  loyaltyPoints,
  loyaltyProvider,
  loyaltyStatus,
  onValidateLoyalty,
  userFirstName,
}: ConsumerSectionProps): JSX.Element | null {
  const { dispensary } = useDispensary();
  const shouldHideLoyaltyPoints = shouldHideLoyaltyPointsForRec(dispensary);
  const disableFasterCheckout = dispensary?.orderTypesConfigV2?.kiosk?.disableFasterCheckout ?? false;

  if (isUserLoggedIn) {
    return (
      <InfoContainer data-testid='kiosk-pro-consumer-info-container'>
        <ThemedEmbeddedAccountIcon aria-hidden='true' focusable='false' />

        <InfoText>
          <span aria-hidden='true'>👋</span>
          &nbsp; Hi, {userFirstName}!
          {hasLoyaltyIntegration && !shouldHideLoyaltyPoints && (
            <Points>
              {loyaltyProvider && <span>{loyaltyProvider}: </span>}
              {loyaltyStatus === 'loading' && 'Loading loyalty…'}
              {loyaltyStatus === 'error' && 'Loyalty unavailable'}
              {loyaltyStatus === 'unavailable' && 'Balance unavailable'}
              {loyaltyStatus === 'validation_required' && (
                <button type='button' onClick={onValidateLoyalty}>
                  Validate by text code
                </button>
              )}
              {(!loyaltyStatus || loyaltyStatus === 'available') && `${loyaltyPoints} pts`}
            </Points>
          )}
        </InfoText>
      </InfoContainer>
    );
  }

  if (hasGuestPhone) {
    return (
      <InfoContainer data-testid='kiosk-pro-consumer-info-container'>
        <ThemedEmbeddedAccountIcon aria-hidden='true' focusable='false' />

        <InfoText>{formatMaskedPhoneNumber(guestPhoneNumber ?? '')}</InfoText>
      </InfoContainer>
    );
  }

  if (disableFasterCheckout) {
    return null;
  }

  return (
    <LoginButton
      data-testid='kiosk-pro-login-button'
      onClick={() => void kioskProModalLogin()}
      aria-busy={createPublicContextSessionLoading}
    >
      <LoginContainer>
        <Badge>
          <LightningIcon aria-hidden='true' focusable='false' /> Faster checkout
        </Badge>

        <LoginText>
          Login to reorder
          <br />
          your favorites
        </LoginText>
      </LoginContainer>
    </LoginButton>
  );
}
